import { containsProfanity } from "./badWords";
import { useRoomsStore } from "../store/useRoomsStore";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  setDoc,
  writeBatch,
  runTransaction as runFirestoreTransaction,
} from "firebase/firestore";
import {
  getDatabase,
  ref,
  get,
  update,
  runTransaction,
} from "firebase/database";
import { db, rtdb } from "../firebase/config"; // adjust based on your setup
import { levelXPTable } from "./levelXPTable";
import { generateCardPack, getIssuableInfinityCards } from "../game/cardPackEngine";
import { GAME_MODES } from "../game/modes";
import { resolveCurseAbsorptionHealthAmount } from "../game/deckEngine";
import { useCardStore } from "../store/useCardsStore";
import { infinityCardImageMap } from "./infinityCardImageMap";
import bronzeBadge from "../assets/badges/bronze_badge.png";
import silverBadge from "../assets/badges/silver_badge.png";
import goldBadge from "../assets/badges/gold_badge.png";
import eliteBadge from "../assets/badges/elite_badge.png";
import legendBadge from "../assets/badges/legend_badge.png";
import mythicBadge from "../assets/badges/mythic_badge.png";
import masterBadge from "../assets/badges/master_badge.png";
import infinityMasterBadge from "../assets/badges/infinty_master_badge.png";
import { ELEMENTS } from "./elements";

// Load the default dictionary once

export function formatNumber(num) {
  //   console.log("formatNumber called with num:", num);
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  }
  return num.toString();
}

export const isValidUsername = (username) => {
  const trimmed = username.trim();
  const usernameRegex = /^[a-zA-Z0-9_]+$/;

  if (!usernameRegex.test(trimmed)) return false;

  const noSpace = trimmed.replace(/\s+/g, "");

  if (containsProfanity(noSpace)) return false;

  if (noSpace.length < 3 || noSpace.length > 20) return false;

  return true;
};

export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim().toLowerCase());
};

export const isValidPassword = (password) => {
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  return passwordRegex.test(password);
};

export const validateAuthInputs = ({ email, username, password }) => {
  //   console.log("email", email, "username", username, "password", password);
  console.log("isValidEmail", isValidEmail(email));
  console.log("isValidPassword", isValidPassword(password));
  console.log("isValidUsername", isValidUsername(username));
  if (!isValidEmail(email))
    return {
      status: false,
      message: "Your Email is not valid. Requirements: Email must be valid.",
    };
  if (!isValidUsername(username))
    return {
      status: false,
      message:
        "Your Username is inappropriate. Requirements: 3-20 characters, no spaces, no special characters, no profanity.",
    };
  if (!isValidPassword(password))
    return {
      status: false,
      message:
        "Your Password is not strong enough. Requirements: At least 8 characters, one uppercase, one lowercase, one number, and one special character.",
    };
  return { status: true, message: "" };
};

export function getColorShades(hexColor) {
  // Helper to convert hex to RGB
  const hexToRgb = (hex) => {
    let parsed = hex.replace(/^#/, "");
    if (parsed.length === 3) {
      parsed = parsed
        .split("")
        .map((c) => c + c)
        .join("");
    }
    const intVal = parseInt(parsed, 16);
    return {
      r: (intVal >> 16) & 255,
      g: (intVal >> 8) & 255,
      b: intVal & 255,
    };
  };

  // Helper to clamp a value between 0 and 255
  const clamp = (val) => Math.max(0, Math.min(255, val));

  // Lighten or darken by a percentage
  const adjust = (color, percent) => {
    const { r, g, b } = color;
    return `#${[
      clamp(Math.round(r + (255 - r) * percent)),
      clamp(Math.round(g + (255 - g) * percent)),
      clamp(Math.round(b + (255 - b) * percent)),
    ]
      .map((c) => c.toString(16).padStart(2, "0"))
      .join("")}`;
  };

  const rgb = hexToRgb(hexColor);
  return {
    light: adjust(rgb, 0.3), // 30% lighter
    main: hexColor,
    dark: adjust(rgb, -0.3), // 30% darker
  };
}

export const formatTime = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  const paddedMinutes = String(minutes).padStart(2, "0");
  const paddedSeconds = String(seconds).padStart(2, "0");

  return `${paddedMinutes}:${paddedSeconds}`;
};

export const getToastMessageFromMove = (move) => {
  if (!move || !move.type) return "";
  if (move?.preventedByMagicBarrier && move?.type === "playCard") {
    return `Magic Barrier blocked "${move?.card?.name || "a card"}" on ${
      move?.target || "the target"
    }.`;
  }

  const {
    attacker,
    type,
    player,
    target,
    defender,
    card,
    damage,
    newStance,
    criticalHit,
  } = move;

  switch (type) {
    case "attack - missed":
      return `⚔️ ${attacker} attacked ${
        target ? target : defender
      } but missed! 👀`;
    // Utils.js -> getToastMessageFromMove switch(...)
    case "attack - reflected":
      return `⚔️ ${
        target ? target : defender
      } reflected the attack! ${attacker} took ${damage} damage.`;
    case "attack":
      return criticalHit
        ? `💥 ${attacker} CRITICALLY attacked ${
            target ? target : defender
          } for ${damage} damage!`
        : `⚔️ ${attacker} attacked ${
            target ? target : defender
          } for ${damage} damage.`;

    case "playCard":
      return `🧙 ${player} played “${card.name}” on ${target}`;

    case "tradeCard":
      return `🔄 ${player} traded a card with ${target}`;

    case "discardCard":
      return `🗑️ ${player} discarded a card`;

    case "stanceChange":
      return `🛡️ ${player} changed stance to ${newStance}`;

    default:
      return `${player} made a move (${type})`;
  }
};

// Utility to convert level string (e.g., "M1") to numeric index
const getLevelIndex = (levelStr) => {
  const index = levelXPTable.findIndex((lvl) => lvl.level === levelStr);
  return index >= 0 ? index : 0;
};

export const getBadgeByLevel = (level) => {
  const numericLevel = level
    ? parseInt(level.toString().replace("M", "10"))
    : 1;

  if (level?.toString().startsWith("M")) {
    return numericLevel >= 5
      ? { badge: infinityMasterBadge, name: "Master T2" }
      : { badge: masterBadge, name: "Master T1" };
  }

  if (numericLevel >= 85) return { badge: mythicBadge, name: "Mythic" };
  if (numericLevel >= 75) return { badge: legendBadge, name: "Legend" };
  if (numericLevel >= 65) return { badge: eliteBadge, name: "Elite" };
  if (numericLevel >= 50) return { badge: goldBadge, name: "Gold" };
  if (numericLevel >= 25) return { badge: silverBadge, name: "Silver" };
  return { badge: bronzeBadge, name: "Bronze" };
};

export const getLevelFromXP = (xp) => {
  let currentLevel = levelXPTable[0];
  for (const levelObj of levelXPTable) {
    if (xp >= levelObj.cumulativeXP) {
      currentLevel = levelObj;
    } else {
      break;
    }
  }
  return currentLevel;
};

// Random utility
export const getRandomInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

// Generate reward based on level
export const generateLevelUpReward = async (
  userData,
  infinityCards,
  characterCards,
  levelObj
) => {
  try {
    if (!levelObj || !levelObj.reward) {
      console.log("No reward for this level.");
      return;
    }

    console.log("There is a reward for this level");

    const userLevel = levelObj.level;

    let chosenType;
    let allInfinity = false;
    const issuableInfinityCards = getIssuableInfinityCards(infinityCards);
    if (levelObj?.infinityOnly) {
      allInfinity = true;
    }

    if (levelObj.issueCardPack) {
      chosenType = "card pack";
    } else {
      const rewardTypes = [
        "finite card",
        "infinity tokens",
        "infinity card",
        // "cosmetic",
        // "avatar",
        // "badge"
      ];
      chosenType = rewardTypes[getRandomInt(0, rewardTypes.length - 1)];
    }

    let rewardData = {
      type: chosenType,
      issuedAt: Date.now(),
      claimed: false,
      reward: null,
      amount: null,
    };

    switch (chosenType) {
      //   case "cosmetic": {
      //     break;
      //   }
      //   case "avatar": {
      //     break;
      //   }
      //   case "badge": {
      //     break;
      //   }
      case "infinty card": {
        if (!issuableInfinityCards || issuableInfinityCards.length === 0) {
          console.warn("No finite cards available.");
          return;
        }
        const availableNonFiniteCards = [...issuableInfinityCards].filter(
          (card) => card.finite === "FALSE" || !card.finite,
        );
        if (!availableNonFiniteCards.length) {
          console.warn("No non-finite cards available.");
          return;
        }
        const card =
          availableNonFiniteCards[
            getRandomInt(0, availableNonFiniteCards.length - 1)
          ];
        rewardData.reward = card;
        rewardData.amount = getRandomInt(1, 4);
        console.log("Reward: finite card");
        break;
      }
      case "finite card": {
        if (!issuableInfinityCards || issuableInfinityCards.length === 0) {
          console.warn("No finite cards available.");
          return;
        }
        const availableFiniteCards = [...issuableInfinityCards].filter(
          (card) => card.finite === "TRUE" || card.finite === true,
        );
        if (!availableFiniteCards.length) {
          console.warn("No finite cards available.");
          return;
        }
        const card =
          availableFiniteCards[getRandomInt(0, availableFiniteCards.length - 1)];
        rewardData.reward = card;
        rewardData.amount = getRandomInt(1, 4);
        console.log("Reward: finite card");
        break;
      }

      case "character card": {
        const ownedChars = userData.nftCards || [];
        if (ownedChars.length === 0) {
          console.warn("No character cards owned.");
          return;
        }
        const char = ownedChars[getRandomInt(0, ownedChars.length - 1)];
        rewardData.reward = char;
        rewardData.amount = getRandomInt(1, 4);
        console.log("Reward: character card");
        break;
      }

      case "infinity tokens": {
        const levelNum =
          parseInt(userLevel.replace("M", "")) || parseInt(userLevel);
        rewardData.reward = { currency: "Infinity Tokens" };
        rewardData.amount = getRandomInt(10, Math.min(100, levelNum * 4));
        console.log("Reward: infinity tokens");
        break;
      }

      case "card pack": {
        const pack = generateCardPack(
          issuableInfinityCards,
          characterCards,
          allInfinity,
          false
        );
        rewardData.reward = { pack };
        rewardData.amount = 1; // optional field, but good for clarity
        console.log("Reward: card pack");
        break;
      }

      default:
        console.warn("Unknown reward type selected.");
        return;
    }

    // Save to Firestore
    const rewardsRef = collection(db, "users", userData.id, "rewards");
    const rewardRef = doc(rewardsRef, 'level_' + String(userLevel).replace(/[^A-Za-z0-9_-]/g, '_'));
    rewardData = await runFirestoreTransaction(db, async tx => {
      const existing = await tx.get(rewardRef);
      if (existing.exists()) return existing.data();
      tx.set(rewardRef, {...rewardData, source:'level_up', level:userLevel});
      return rewardData;
    });

    console.log("Reward issued:", rewardData);
    return rewardData;
  } catch (error) {
    console.error("Error generating level-up reward:", error);
  }
};

// Main function to check and update user level
export const checkAndUpdateLevel = async (
  userData,
  newXP,
  infinityCards,
  characterCards
) => {
  try {
    const userRef = doc(db, "users", userData.id);

    const prevLevelObj = getLevelFromXP(userData.xp || 0);
    const newLevelObj = getLevelFromXP(newXP);

    const prevLevelIndex = getLevelIndex(prevLevelObj.level);
    const newLevelIndex = getLevelIndex(newLevelObj.level);

    if (newLevelIndex > prevLevelIndex) {
      // Update user's level
      await runFirestoreTransaction(db, async tx => {
        const latest = await tx.get(userRef);
        const currentLevel = getLevelFromXP(Math.max(newXP, Number(latest.data()?.xp) || 0));
        if (getLevelIndex(latest.data()?.level || '1') < getLevelIndex(currentLevel.level)) tx.update(userRef, {level:currentLevel.level});
      });

      console.log(
        `🔍 Checking level-up from ${prevLevelObj.level} to ${newLevelObj.level}`
      );

      // Issue rewards for each level gained that has a reward
      for (let i = prevLevelIndex + 1; i <= newLevelIndex; i++) {
        const level = levelXPTable[i];
        if (level.reward) {
          console.log(
            `🎯 Level ${level.level} includes a reward. Card pack: ${level.issueCardPack}`
          );
          const issuedReward = await generateLevelUpReward(
            userData,
            infinityCards,
            characterCards,
            level
          );
          if (!issuedReward) throw new Error("Your level reward is pending. Retry the original result.");
        }
      }

      console.log(
        `User ${userData.id} leveled up from ${prevLevelObj.level} to ${newLevelObj.level}`
      );
    }

    return newLevelObj;
  } catch (error) {
    console.error("Error in checkAndUpdateLevel:", error);
    return null;
  }
};

export const getQuestProgress = (quest, questLog = []) => {
  let progress = 0;
  const startedAt = quest.startedAt || 0;

  questLog
    .filter((log) => log.timestamp >= startedAt)
    .forEach((log) => {
      const stats = log.questStats || {};

      switch (quest.type) {
        case "damage":
          progress += stats.totalDamageDealt || 0;
          break;

        case "playCards":
          progress += stats.totalCardsPlayed || 0;
          break;

        case "cardType":
          progress +=
            stats.typesOfCardsPlayed?.filter((type) => type === quest.subtype)
              .length || 0;
          break;

        case "elementWins":
          if (
            stats.matchWon &&
            stats.characterElement?.toLowerCase() ===
              quest.subtype?.toLowerCase()
          ) {
            progress += 1;
          }
          break;

        case "rarityWins":
          if (
            stats.matchWon &&
            stats.characterRarity?.toLowerCase() ===
              quest.subtype?.toLowerCase()
          ) {
            progress += 1;
          }
          break;

        default:
          break;
      }
    });

  return progress;
};

export const checkAndCompleteQuests = async (userData) => {
  try {
    const { id: userId, quests = [], questLog = [] } = userData;
    if (!userId || quests.length === 0) return [];

    const rewardsRef = collection(db, "users", userId, "rewards");
    const questsRef = collection(db, "users", userId, "quests");

    const batch = writeBatch(db);
    const completedQuests = [];

    for (const quest of quests) {
      if (quest.completed) continue;

      const progress = getQuestProgress(quest, questLog);
      if (progress >= quest.goal) {
        const questDocRef = doc(questsRef, quest.id);

        // ✅ Mark quest as complete
        batch.update(questDocRef, {
          completed: true,
          completedAt: Date.now(),
        });

        // ✅ XP Reward (separate reward)
        const xpReward = {
          type: "quest reward",
          reward: {
            currency: "XP",
          },
          amount: quest.reward.xp,
          claimed: false,
          issuedAt: Date.now(),
          questId: quest.id,
        };

        await addDoc(rewardsRef, xpReward);

        // ✅ Token Reward (separate reward)
        const tokenReward = {
          type: "quest reward",
          reward: {
            currency: "Infinity Tokens",
          },
          amount: quest.reward.tokens,
          claimed: false,
          issuedAt: Date.now(),
          questId: quest.id,
        };

        await addDoc(rewardsRef, tokenReward);

        completedQuests.push({
          quest,
          rewards: [xpReward, tokenReward],
        });

        console.log(`🏁 Quest Completed: ${quest.title}`);
      }
    }

    await batch.commit();

    return completedQuests;
  } catch (err) {
    console.error("❌ Error auto-completing quests:", err);
    return [];
  }
};

export const updatePlayerPoints = async (roomId, playerId, updates) => {
  try {
    const db = getDatabase();
    const pointsRef = ref(db, `rooms/${roomId}/matchData/playerPoints`);
    const snap = await get(pointsRef);
    if (!snap.exists()) return;

    const existingData = snap.val();
    const playerData = existingData[playerId];
    if (!playerData) return;

    const { questStats = {}, ...pointUpdates } = updates;

    // Accumulate points with caps
    const updatedPointData = Object.entries(pointUpdates).reduce(
      (acc, [key, value]) => {
        acc[key] = Math.min(
          key === "damageDealtPoints"
            ? 50
            : key === "playedCardPoints"
            ? 50
            : key === "discardCardPoints" || key === "tradeCardPoints"
            ? 30
            : value,
          (playerData[key] ?? 0) + value
        );
        return acc;
      },
      {}
    );

    // Quest stat accumulation
   const updatedQuestStats = {
  ...(playerData.questStats || {}),
  ...Object.entries(questStats).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      // ✅ keep duplicates: append instead of Set
      const prevArr = Array.isArray(playerData.questStats?.[key])
        ? playerData.questStats[key]
        : [];
      acc[key] = [...prevArr, ...value];
    } else {
      acc[key] = (playerData.questStats?.[key] || 0) + value;
    }
    return acc;
  }, {}),
};


    const updatedPlayerData = {
      ...playerData,
      ...updatedPointData,
      questStats: updatedQuestStats,
    };

    await update(pointsRef, {
      [playerId]: updatedPlayerData,
    });
  } catch (err) {
    console.error("❌ Failed to update player points (RTDB):", err);
  }
};

//!V2 ===
// export const updatePlayerPoints = async (roomId, playerId, updates) => {
//   try {
//     const db = getDatabase();
//     const playerRef = ref(db, `rooms/${roomId}/matchData/playerPoints/${playerId}`);

//     // Per-key caps (extend as needed)
//     const CAPS = {
//       damageDealtPoints: 50,
//       playedCardPoints: 50,
//       discardCardPoints: 30,
//       tradeCardPoints: 30,
//     };

//     await runTransaction(playerRef, (current) => {
//       // Initialize player node if missing
//       if (current == null || typeof current !== "object") {
//         current = { questStats: {} };
//       }
//       if (current.questStats == null || typeof current.questStats !== "object") {
//         current.questStats = {};
//       }

//       const { questStats = {}, ...pointUpdates } = updates || {};

//       // --- Accumulate point keys with caps ---
//       for (const [key, rawVal] of Object.entries(pointUpdates)) {
//         const add = Number(rawVal ?? 0);
//         if (!Number.isFinite(add) || add === 0) continue;

//         const prev = Number(current[key] ?? 0);
//         const next = prev + add;
//         const cap = CAPS[key];

//         current[key] = cap != null ? Math.min(cap, next) : next;
//       }

//       // --- Accumulate quest stats ---
//       const existingQS = current.questStats;

//       for (const [qKey, qVal] of Object.entries(questStats)) {
//         if (Array.isArray(qVal)) {
//           // Keep duplicates: append incoming array to existing (or empty)
//           const prevArr = Array.isArray(existingQS[qKey]) ? existingQS[qKey] : [];
//           existingQS[qKey] = [...prevArr, ...qVal];
//         } else {
//           // Numeric accumulation (e.g., totalCardsPlayed)
//           const prevNum = Number(existingQS[qKey] ?? 0);
//           const addNum = Number(qVal ?? 0);
//           existingQS[qKey] = prevNum + (Number.isFinite(addNum) ? addNum : 0);
//         }
//       }

//       return current;
//     });
//   } catch (err) {
//     console.error("❌ Failed to update player points (RTDB):", err);
//   }
// };

export const addNewLogToQuestLogs = async (userId, questStats, timestamp) => {
  try {
    if (!userId || !questStats) return;

    const logTime = timestamp ?? Date.now();
    const questLogRef = doc(db, "users", userId, "questLog", `${logTime}`);

    const existing = await getDoc(questLogRef);

    if (!existing.exists()) {
      await setDoc(questLogRef, {
        questStats,
        timestamp: logTime,
      });
    }

    console.log(`📘 Quest log saved for ${userId} at ${logTime}`);
  } catch (err) {
    console.error("❌ Failed to save quest log:", err);
  }
};

export const getPlayerStatsDisplayData = (player, matchData) => {
  if (!player || !matchData) return {};

  const { avatar, character, name, id } = player;

  const moves = matchData?.moves || [];
  const eliminated =
    matchData?.players?.filter((p) => !!p.character.isDead) || [];

  // 1. Avatar fallback
  const avatarImage = character?.imageUrl;

  // 2. Total moves: this player either played or attacked
  const movesByPlayer = moves.filter(
    (m) => m.player === name || m.attacker === name
  );
  const movesCount = movesByPlayer.length;

  // 3. Eliminations caused by this player
  const eliminationsCount = eliminated.filter(
    (entry) => entry?.character?.eliminatedBy?.id === id
  ).length;

  // 4. Total damage dealt by this player
  const damageDealt = moves
    .filter((m) => m.attacker === name && typeof m.damage === "number")
    .reduce((sum, m) => sum + m.damage, 0);

  // 5. Critical hits
  const criticalHits = moves.filter(
    (m) => m.attacker === name && m.criticalHit
  ).length;

  // 6. Cards played
  const cardPlays = moves.filter((m) => m.player === name && m.card).length;

  return {
    avatar: avatarImage,
    movesCount,
    eliminationsCount,
    damageDealt,
    criticalHits,
    cardPlays,
  };
};

export const getCardImageByName = (name, isCharacter = false) => {
  const snakeName = name
    ?.trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[\s\-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "");

  let characterCardImageMap; //!UPDATE....

  const imageMap = isCharacter ? characterCardImageMap : infinityCardImageMap;
  const image = imageMap[snakeName];
  //   if (name === "Archer's Charm") {
  //     console.log("Archer's Charm => ", snakeName);
  //     console.log("Archer's Charm image => ", imageMap);
  //   }

  // if (!image)
  //   console.warn(`⚠️ No image found for card: ${name} → ${snakeName}`);

  return image ?? null;
};

// @shared/utils/elementalEffectiveness.js

export { ELEMENTS };

export const ELEMENT_EFFECTIVENESS = {
  fire: { grass: 1, ice: 1, void: 1, water: -1, rock: -1 },
  water: { fire: 1, rock: 1, grass: -1, electric: -1 },
  rock: { electric: 1, ice: 1, grass: -1, water: -1 },
  grass: { water: 1, rock: 1, fire: -1, ice: -1 },
  air: { grass: 1, fire: 1, rock: -1, electric: -1 },
  ice: { grass: 1, air: 1, fire: -1, rock: -1 },
  electric: { water: 1, air: 1, void: 1, rock: -1, grass: -1 },
  void: {
    fire: 1,
    water: 1,
    grass: 1,
    air: 1,
    ice: 1,
    electric: 1,
    void: -1,
  },
};

export function getElementalBonus(attackerElement, defenderElement) {
  const map = ELEMENT_EFFECTIVENESS[attackerElement];
  if (!map) return 0;
  return map[defenderElement] || 0;
}

export function applyBoosts(character) {
  const boosts = character.boosts || {};
  return {
    ...character,
    health: Number(character.health) + (boosts.health || 0),
    attack: Number(character.attack) + (boosts.attack || 0),
    defense: Number(character.defense) + (boosts.defense || 0),
    accuracy: Number(character.accuracy) + (boosts.accuracy || 0),
  };
}

export function getAvailableBoosts(character, selectedBoostsForThisChar = {}) {
  const baseOwned = Number(character.amountOwned || 1);
  const existingBoosts = character.boosts || {};
  const existingBoostTotal = Object.values(existingBoosts).reduce(
    (a, b) => a + b,
    0
  );
  const selectedBoostTotal = Object.values(selectedBoostsForThisChar).reduce(
    (a, b) => a + b,
    0
  );

  // Max 5 total boosts allowed per character
  const remainingBoostsByCap = 5 - (existingBoostTotal + selectedBoostTotal);

  // Max boosts user is allowed to spend is amountOwned - 1 (must keep 1)
  const remainingByOwned = baseOwned - 1 - selectedBoostTotal;

  return Math.max(0, Math.min(remainingBoostsByCap, remainingByOwned));
}

export const maybeActivateCurseAbsorptionFromHand = ({
  recipient,
  hands,
}) => {
  const recipientId = recipient?.id;
  if (!recipientId) {
    return { activated: false, hands };
  }

  const activeTurns = Number(recipient?.character?.curseAbsorptionTurns ?? 0);
  if (activeTurns > 0) {
    return { activated: false, hands };
  }

  const recipientHand = Array.isArray(hands?.[recipientId]) ? hands[recipientId] : [];
  const curseIdx = recipientHand.findIndex((c) =>
    ["curse absorption", "curse absorbtion"].includes(
      String(c?.name || "").trim().toLowerCase()
    )
  );

  if (curseIdx === -1) {
    return { activated: false, hands };
  }

  const curseCard = recipientHand[curseIdx];
  const parsedDuration = parseInt(
    String(curseCard?.turnsActive || "").match(/\d+/)?.[0] || "0",
    10
  );
  const duration = parsedDuration || 1;
  const nextHand = recipientHand.filter((_, i) => i !== curseIdx);

  return {
    activated: true,
    duration,
    consumedCard: curseCard,
    hands: {
      ...(hands || {}),
      [recipientId]: nextHand,
    },
  };
};

export const applyBlindfoldToHand = (hand = []) =>
  hand.map((card) => ({
    ...card,
    isBlindfolded: true,
  }));

export const normalizeEffectArea = (raw = "") => {
  const normalized = String(raw).trim().toLowerCase().replace(/\s+/g, "");
  if (
    normalized === "turnloss" ||
    normalized === "turnskip" ||
    normalized === "skippedturns"
  ) {
    return "turnLoss";
  }
  if (normalized.startsWith("def")) return "defense";
  if (normalized.startsWith("acc")) return "accuracy";
  if (normalized.startsWith("atk") || normalized.startsWith("att")) {
    return "attack";
  }
  if (normalized.startsWith("hp") || normalized.startsWith("health")) {
    return "health";
  }
  return normalized;
};

export const toFiniteNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

export const specialtyCardEffect = ({
  card,
  actingPlayer,
  targetPlayer,
  players,
  hands,
  decks,
  matchRef,
  roomId,
  mode,
}) => {
  const updatedPlayers = [...players];
  const actingIndex = updatedPlayers.findIndex(
    (p) => p.id === actingPlayer?.id
  );
  const targetIndex = updatedPlayers.findIndex(
    (p) => p.id === targetPlayer?.id
  );
  let forceEndTurn = false;
  let triggeredHelpCard = null;
  const normalizedCardName = String(card?.name || "")
    .trim()
    .toLowerCase()
    .replace(/[’'`]/g, "")
    .replace(/\s+/g, " ");
  const isLordsRuneStone = [
    "lords rune stone",
    "lord runes stone",
    "lord rune stone",
  ].includes(normalizedCardName);

  // Helper: normalize area & target strings
  const norm = (s = "") => String(s).trim().toLowerCase();
  const normalizeTarget = (raw = "") => {
    const s = norm(raw);
    if (["both", "all", "everyone"].includes(s)) return "both";
    if (["self", "me", "owner"].includes(s)) return "self";
    return "target";
  };
  const normalizeDefenseStance = (player) => {
    if (!player?.character) return player;
    const nextPlayer = { ...player };
    const nextChar = { ...nextPlayer.character };
    if (Number(nextChar.defense ?? 0) <= 0) {
      nextChar.defense = 0;
      nextChar.stance = "attack";
    }
    nextPlayer.character = nextChar;
    return nextPlayer;
  };
  const getBuffTotal = (character, area) =>
    Array.isArray(character?.buffs?.[area])
      ? character.buffs[area].reduce(
          (sum, buff) => sum + Number(buff?.amt || 0),
          0
        )
      : 0;
  const getEffectiveStatValue = (character, area) => {
    const normalizedArea = normalizeEffectArea(area);
    const baseValue = Number(character?.[normalizedArea] ?? 0);
    if (normalizedArea === "attack" || normalizedArea === "accuracy") {
      return baseValue + getBuffTotal(character, normalizedArea);
    }
    return baseValue;
  };

  // Priority legacy behavior: this card swaps player slots and ends turn.
  // Handle it before generic V2 parsing so metadata shape cannot bypass it.
  if (isLordsRuneStone) {
    if (actingIndex !== -1 && targetIndex !== -1) {
      const temp = updatedPlayers[actingIndex];
      updatedPlayers[actingIndex] = updatedPlayers[targetIndex];
      updatedPlayers[targetIndex] = temp;
      forceEndTurn = true;
    }
    return {
      players: updatedPlayers.map(normalizeDefenseStance),
      hands,
      decks,
      forceEndTurn,
    };
  }

  if (normalizedCardName === "the bards song") {
    const recipientPlayer =
      targetPlayer && targetPlayer?.id ? targetPlayer : actingPlayer;
    const recipientIndex = updatedPlayers.findIndex(
      (p) => p.id === recipientPlayer?.id
    );
    const duration = /instant/i.test(String(card?.turnsActive || ""))
      ? 0
      : parseInt(String(card?.turnsActive || "").match(/\d+/)?.[0] || "0", 10);

    const recipientIndexes =
      mode === GAME_MODES.TEAMS && recipientPlayer?.team
        ? updatedPlayers
            .map((player, index) =>
              player?.team === recipientPlayer.team &&
              !player?.character?.isDead
                ? index
                : -1
            )
            .filter((index) => index !== -1)
        : recipientIndex !== -1
        ? [recipientIndex]
        : [];

    recipientIndexes.forEach((index) => {
      const playerCopy = { ...updatedPlayers[index] };
      const char = {
        ...(playerCopy.character || {}),
        buffs: { ...(playerCopy.character?.buffs || {}) },
      };

      char.health = Math.max(0, Number(char.health || 0) + 3);
      if (!Array.isArray(char.buffs.attack)) char.buffs.attack = [];
      char.buffs.attack.push({
        amt: 2,
        duration,
        icon: card?.imageUrl,
        name: card?.name || "The Bard's Song",
      });

      playerCopy.character = char;
      updatedPlayers[index] = playerCopy;
    });

    return {
      players: updatedPlayers.map(normalizeDefenseStance),
      hands,
      decks,
      forceEndTurn,
      triggeredHelpCard,
    };
  }

  // === V2: generic multi-effect specialty cards ===
  if (Array.isArray(card.areaAffected) && card.areaAffected.length > 1) {
    const effectText = String(card.effect || "");
    // Split into meaningful sentences; collapse multiple spaces; drop empties
    const sentences = effectText
      .split(/[.!?]/)
      .map((s) => s.trim())
      .filter(Boolean);

    // Prefer the number that follows "by", else the first number that is NOT followed by "turn"
    const extractPrimaryAmount = (s) => {
      const lower = s.toLowerCase();
      const byMatch = lower.match(/\bby\s+(-?\d+)/i);
      if (byMatch) return parseInt(byMatch[1], 10);

      // Find first integer not immediately followed by "turn"
      const all = [...lower.matchAll(/-?\d+/g)];
      for (const m of all) {
        const after = lower.slice(m.index + m[0].length).trim();
        if (!after.startsWith("turn")) return parseInt(m[0], 10);
      }
      // Fallback: first number (even if it's "turns")
      return all.length ? parseInt(all[0][0], 10) : 0;
    };

    const sentenceSign = (s) => {
      const t = s.toLowerCase();
      const neg =
        /\b(lose|loses|lost|reduce|reduced|reduces|decrease|decreased|debuff)\b/.test(
          t
        );
      const pos =
        /\b(gain|gains|gained|increase|increased|increases|boost|buff)\b/.test(
          t
        );
      if (neg && !pos) return -1;
      if (pos && !neg) return 1;
      // If both appear, bias by which appears first
      const firstNeg = t.search(
        /\b(lose|loses|lost|reduce|reduced|reduces|decrease|decreased|debuff)\b/
      );
      const firstPos = t.search(
        /\b(gain|gains|gained|increase|increased|increases|boost|buff)\b/
      );
      if (firstNeg !== -1 && firstPos !== -1)
        return firstNeg < firstPos ? -1 : 1;
      // Default: debuff
      return -1;
    };

    const duration = /instant/i.test(String(card.turnsActive))
      ? 0
      : parseInt(String(card.turnsActive || "").match(/\d+/)?.[0] || "0", 10);

    // Build normalized {area, target}
    const areaPairs = card.areaAffected.map((entry) => {
      const [rawArea, rawTarget = "target"] = String(entry || "")
        .replace(")", "")
        .split("(")
        .map((x) => x.trim());
      return {
        area: normalizeEffectArea(rawArea),
        target: normalizeTarget(rawTarget || "target"),
      };
    });

    // Apply each area using its own sentence (or the last one if we run out)
    areaPairs.forEach(({ area, target }, i) => {
      const sentence = sentences[Math.min(i, sentences.length - 1)] || "";
      const amtFromSentence = extractPrimaryAmount(sentence);
      const sign = area === "turnLoss" ? 1 : sentenceSign(sentence);
      let amount = amtFromSentence * sign;

      const recipients =
        target === "self"
          ? ["self"]
          : target === "both"
          ? ["self", "target"]
          : ["target"];

      const isInstant = duration === 0 || /instant/i.test(effectText);

      for (const who of recipients) {
        const isSelf = who === "self";
        const idx = isSelf ? actingIndex : targetIndex;
        if (idx === -1) continue;

        const player = { ...updatedPlayers[idx] };
        if (!player.character) player.character = {};

        if (area === "health" && Number(amount) < 0) {
          const helpResult = maybeActivateCurseAbsorptionFromHand({
            recipient: player,
            hands,
          });
          if (helpResult.activated) {
            hands = helpResult.hands;
            triggeredHelpCard = helpResult.consumedCard || triggeredHelpCard;
            player.character = {
              ...player.character,
              curseAbsorptionTurns: Math.max(
                Number(player.character?.curseAbsorptionTurns || 0),
                helpResult.duration
              ),
            };
          }
        }

        const appliedAmount =
          area === "health"
            ? resolveCurseAbsorptionHealthAmount(player.character, amount)
            : amount;

        if (area === "turnLoss") {
          const turnLossToAdd = Math.abs(amtFromSentence || amount || 0);
          player.character.turnLoss =
            (player.character.turnLoss || 0) + turnLossToAdd;
        } else if (isInstant && !["attack", "accuracy"].includes(area)) {
          const current = Number(player.character[area] || 0);
          let next = current + appliedAmount;
          if (["health", "defense"].includes(area)) next = Math.max(0, next); // clamp ≥ 0
          player.character[area] = next;
          if (area === "defense" && next <= 0) {
            player.character.defense = 0;
            player.character.stance = "attack";
          }
          if (area === "health" && next <= 0 && !player.character.isDead) {
            player.character.health = 0;
            player.character.isDead = true;
            player.character.eliminatedBy = {
              name: actingPlayer?.name || "Player",
              id: actingPlayer?.id || null,
              character: actingPlayer?.character?.name || "Character",
              cardName: card?.name || null,
              eliminatedAt: Date.now(),
            };
          }
        } else {
          if (!player.character.buffs) player.character.buffs = {};
          if (!player.character.buffs[area]) player.character.buffs[area] = [];
          const buff = { amt: amount, duration };
          if (area === "health" && Number(amount) < 0) {
            buff.source = {
              id: actingPlayer?.id || null,
              name: actingPlayer?.name || "Player",
              character: actingPlayer?.character?.name || "Character",
              cardName: card?.name || null,
              appliedAt: Date.now(),
            };
          }
          player.character.buffs[area].push(buff);
        }

        updatedPlayers[idx] = player;
      }
    });

    return {
      players: updatedPlayers.map(normalizeDefenseStance),
      hands,
      decks,
      forceEndTurn,
      triggeredHelpCard,
    };
  }

  // === FALLBACK: legacy specialty cards ===

  const cardName = normalizedCardName;
  const hasArea = (needle) =>
    (card.areaAffected || []).some((a) =>
      String(a || "")
        .toLowerCase()
        .includes(needle.toLowerCase())
    );

  switch (cardName) {
    case "rivered return":
      if (!targetPlayer.character.buffs) {
        targetPlayer.character.buffs = {};
      }

      ["health", "defense", "accuracy", "attack"].forEach((stat) => {
        if (targetPlayer.character.buffs?.[stat]) {
          delete targetPlayer.character.buffs[stat];
        }
      });

      targetPlayer.character.attack = targetPlayer.character.startingAttack;
      targetPlayer.character.defense = targetPlayer.character.startingDefense;
      targetPlayer.character.health = targetPlayer.character.startingHealth;
      targetPlayer.character.accuracy = targetPlayer.character.startingAccuracy;

      updatedPlayers[targetIndex] = targetPlayer;
      break;

    case "lords rune stone":
    case "lord runes stone":
    case "lord rune stone":
      if (actingIndex !== -1 && targetIndex !== -1) {
        const temp = updatedPlayers[actingIndex];
        updatedPlayers[actingIndex] = updatedPlayers[targetIndex];
        updatedPlayers[targetIndex] = temp;
        forceEndTurn = true;
      }
      break;

    case "stoned": {
      const originalHand = hands?.[targetPlayer.id] || [];

      const stonedHand = originalHand.map((card, index) => ({
        id: `stone-${Date.now()}-${index}`,
        name: "Stone",
        description: "This card is unusable. You must discard it.",
        effect: "none",
        type: "curse",
        imageUrl:
          "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/Infinity%20Card%20Images%2FStoned-hand.png?alt=media&token=5badea35-9c11-4dce-adc5-d9aaa9b1faca",
        turnsActive: "Instant",
        areaAffected: [],
        isStone: true,
        bgColor1: "#b5b6ba",
        bgColor2: "#1f2740",
        finite: "TRUE",
      }));

      hands = {
        ...hands,
        [targetPlayer.id]: [...stonedHand],
      };

      break;
    }
    case "soul swap": {
      if (!actingPlayer?.character || !targetPlayer?.character) break;

      // Stats we care about
      const KEYS = ["health", "defense", "attack", "accuracy"];

      // Shallow copies of character objects to avoid mutation bugs
      let actingChar = { ...actingPlayer.character };
      let targetChar = { ...targetPlayer.character };

      // 1. Swap base stats (ignore stance)
      KEYS.forEach((key) => {
        const temp = actingChar[key];
        actingChar[key] = targetChar[key];
        targetChar[key] = temp;
      });

      // 2. Swap buffs object safely
      const tempBuffs = { ...(actingChar.buffs || {}) };
      actingChar.buffs = { ...(targetChar.buffs || {}) };
      targetChar.buffs = tempBuffs;

      // 3. Write updated characters back into players
      actingPlayer.character = actingChar;
      targetPlayer.character = targetChar;

      updatedPlayers[actingIndex] = actingPlayer;
      updatedPlayers[targetIndex] = targetPlayer;

      break;
    }
    case "cloak of nullification": {
      const recipientIsSelf =
        !targetPlayer || actingPlayer?.id === targetPlayer?.id;
      const idx = recipientIsSelf ? actingIndex : targetIndex;
      if (idx === -1) break;

      const playerCopy = { ...updatedPlayers[idx] };
      const char = { ...(playerCopy.character || {}) };

      char.cloakedTurns = 4; // simple key/value; stance/other fields untouched
      playerCopy.character = char;

      updatedPlayers[idx] = playerCopy;
      break;
    }
    case "magic barrier": {
      const recipientIsSelf =
        !targetPlayer || actingPlayer?.id === targetPlayer?.id;
      const idx = recipientIsSelf ? actingIndex : targetIndex;
      if (idx === -1) break;

      const playerCopy = { ...updatedPlayers[idx] };
      const char = { ...(playerCopy.character || {}) };

      char.magicBarrierTurns = 4;
      playerCopy.character = char;

      updatedPlayers[idx] = playerCopy;
      break;
    }
    case "blindfold": {
      const recipientIsSelf =
        !targetPlayer || actingPlayer?.id === targetPlayer?.id;
      const idx = recipientIsSelf ? actingIndex : targetIndex;
      if (idx === -1) break;

      const playerId = updatedPlayers[idx]?.id;
      const blindfoldedHand = applyBlindfoldToHand(hands?.[playerId] || []);
      hands = {
        ...(hands || {}),
        [playerId]: blindfoldedHand,
      };
      break;
    }
    case "pocket sand": {
      if (targetIndex === -1) break;

      const playerCopy = { ...updatedPlayers[targetIndex] };
      const char = { ...(playerCopy.character || {}) };
      const duration = /instant/i.test(String(card?.turnsActive || ""))
        ? 0
        : parseInt(String(card?.turnsActive || "").match(/\d+/)?.[0] || "0", 10);
      const currentAccuracy = getEffectiveStatValue(char, "accuracy");
      const blindAmount = -currentAccuracy;

      if (!char.buffs) char.buffs = {};
      if (!Array.isArray(char.buffs.accuracy)) char.buffs.accuracy = [];

      if (blindAmount !== 0) {
        char.buffs.accuracy.push({
          amt: blindAmount,
          duration,
          icon: card?.imageUrl,
          name: card?.name || "Pocket Sand",
        });
      }

      playerCopy.character = char;
      updatedPlayers[targetIndex] = playerCopy;
      break;
    }
    case "curse absorption": {
      const recipientIsSelf =
        !targetPlayer || actingPlayer?.id === targetPlayer?.id;
      const idx = recipientIsSelf ? actingIndex : targetIndex;
      if (idx === -1) break;

      const playerCopy = { ...updatedPlayers[idx] };
      const char = { ...(playerCopy.character || {}) };
      const parsedDuration = parseInt(
        String(card?.turnsActive || "").match(/\d+/)?.[0] || "0",
        10
      );

      char.curseAbsorptionTurns = Math.max(
        Number(char.curseAbsorptionTurns || 0),
        parsedDuration || 1
      );

      playerCopy.character = char;
      updatedPlayers[idx] = playerCopy;
      break;
    }
    // Utils.js -> inside specialtyCardEffect(...) legacy switch:
    case "sleight of hand":
    case "slight of hand":
    // fallthrough
    case "": {
      // If name didn't match any case but area says Player Hand, treat it as hand swap
      if (
        cardName &&
        cardName !== "sleight of hand" &&
        cardName !== "slight of hand" &&
        !hasArea("player hand")
      ) {
        break;
      }
      const actingId = actingPlayer?.id;
      const targetId = targetPlayer?.id;

      // Deep copies
      let actingHand = [...(hands?.[actingId] || [])];
      let targetHand = [...(hands?.[targetId] || [])];

      // Helper: robust match for the exact played instance (handles cloned ids/_uuid)
      const isSamePlayedCard = (c) =>
        !!c &&
        (c.id === card.id ||
          c._uuid === card._uuid ||
          (String(c.name).toLowerCase() === String(card.name).toLowerCase() &&
            c.type === card.type &&
            (c.imageUrl ? c.imageUrl === card.imageUrl : true)));

      // 1) Remove the played card from BOTH hands (defensive)
      actingHand = actingHand.filter((c) => !isSamePlayedCard(c));
      // targetHand = targetHand.filter((c) => !isSamePlayedCard(c));

      // Swap hands (post-removal)
      hands = {
        ...hands,
        [actingId]: targetHand,
        [targetId]: actingHand,
      };

      // 2) Final sanitize: ensure no Sleight/Slight card leaked into either hand
      const isAnySleight = (c) =>
        !!c &&
        ["sleight of hand", "slight of hand"].includes(
          String(c.name || "")
            .trim()
            .toLowerCase()
        );
      hands[actingId] = (hands[actingId] || []).filter(
        (c) => !isSamePlayedCard(c) && !isAnySleight(c)
      );
      hands[targetId] = (hands[targetId] || []).filter(
        (c) => !isSamePlayedCard(c) && !isAnySleight(c)
      );

      // Nothing else changes; let the board handle write + move log
      break;
    }

    case "medallion of luck": {
      const idx = actingIndex; // card is played on self; safe if you later allow target=self
      if (idx === -1) break;

      const p = { ...updatedPlayers[idx] };
      const c = { ...(p.character || {}) };

      c.luck = Number(c.luck || 0) + 1; // ← bump luck by 1
      p.character = c;
      updatedPlayers[idx] = p;
      break;
    }
    case "taunt": {
      if (targetIndex === -1) break;

      const playerCopy = { ...updatedPlayers[targetIndex] };
      const char = { ...(playerCopy.character || {}) };
      const currentStance = String(char.stance || "defense").toLowerCase();

      char.stance = currentStance === "attack" ? "defense" : "attack";
      playerCopy.character = char;
      updatedPlayers[targetIndex] = playerCopy;
      break;
    }

    default:
      break;
  }

  return {
    players: updatedPlayers.map(normalizeDefenseStance),
    hands,
    decks,
    forceEndTurn,
    triggeredHelpCard,
  };
};

// Direction-aware next/prev living player finder.
// If reversed === false → step +1 (clockwise).
// If reversed === true  → step -1 (counter-clockwise).
export const getNextLivingPlayerIndexDirectional = (
  playersArr,
  fromIndex,
  reversed = false
) => {
  const n = playersArr?.length || 0;
  if (n === 0) return 0;

  // guard: clamp starting index
  let idx = ((fromIndex % n) + n) % n;
  const step = reversed ? -1 : 1;

  for (let i = 1; i <= n; i++) {
    const candidate = (idx + step * i + n) % n;
    const p = playersArr[candidate];
    if (p && !p?.character?.isDead && (p?.character?.health ?? 1) > 0) {
      return candidate;
    }
  }

  // fallback: if somehow all are dead or something odd, return original
  return idx;
};

// Utils.js (export it)
export function getSpinWheelNumbers(
  base = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  luck = 0
) {
  const L = Math.max(0, Number(luck || 0));
  const arr = [...base];

  // number of slots to replace: 2 per luck, capped
  const replaceCount = Math.min(arr.length, 2 * L);
  if (replaceCount === 0) return arr;

  for (let i = 0; i < replaceCount; i++) {
    const idx = arr.length - 1 - i; // start from the highest values
    arr[idx] = 1;
  }
  return arr;
}
